import { Component, OnInit, Inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { environment } from '../../../environments/environment';
import { ChangeDetectorRef } from '@angular/core';

// Modern Services
import { LoginService } from '../../services/login.service';
import { UsersService } from '../../services/users.service';
import { CommonService } from '../../services/common.service';
import { LoggerService } from '../../services/logger.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit {
  user_details: any = {};
  isVisibleForgotPasswordPanel: boolean = false;
  user: any = {};
  isProcessing: boolean = false;
  isNavigating: boolean = false;
  loginButtonText: string = "Sign In";
  intervals: any[] = [];
  cookiesAllowed: string | null;
  allPreferences: any;
  // Change the declaration at the top
  copyrightDetails: any = {};
  store_manager: boolean = false;
  login_error: boolean = false;
  login_message: string = '';
  userauthenticate: boolean = false;
  private logger: any;

  constructor(
    private loginService: LoginService,
    private usersService: UsersService,
    private common: CommonService, // <--- Replaced the 3 separate memory services
    private loggerService: LoggerService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) {
    this.logger = this.loggerService.getInstance("LoginController");

    // Using your new common service instead of the clunky vanilla JS getCookie function!
    this.cookiesAllowed = this.common.CookieMemory.API.Get('cookiesAllowed');
  }

  ngOnInit(): void {


    this.findByCopyright();
    this.isNavigating = true;

    let token = this.route.snapshot.queryParams['token'] || this.route.snapshot.queryParams['&token'];
    let sessionId = this.route.snapshot.queryParams['sessionId'] || this.route.snapshot.queryParams['session_id'] || this.route.snapshot.queryParams['sessionid'];
    let moduleId = this.route.snapshot.queryParams['moduleid'] || this.route.snapshot.queryParams['moduleId'] || this.route.snapshot.queryParams['module_id'];

    // Fallback: parse manually from window.location.href to support hash-routing and "?&token=" safely
    if (typeof window !== 'undefined') {
      const href = window.location.href;
      const getParam = (paramName: string) => {
        const regex = new RegExp('[?&]+' + paramName + '=([^&#]*)', 'i');
        const match = regex.exec(href);
        return match ? decodeURIComponent(match[1]) : null;
      };

      if (!token) token = getParam('token');
      if (!sessionId) sessionId = getParam('sessionId') || getParam('session_id') || getParam('sessionid');
      if (!moduleId) moduleId = getParam('moduleid') || getParam('moduleId') || getParam('module_id');
    }

    if (token && sessionId) {
      console.log('[SSO Debug] Token and SessionID successfully extracted from URL!', { token, sessionId, moduleId });
      // Save to both SessionMemory and CookieMemory so that the authInterceptor has access to them!
      this.common.SessionMemory.API.Post('user.session', sessionId);
      this.common.SessionMemory.API.Post('user.token', token);
      this.common.CookieMemory.API.Post('user.session', sessionId);
      this.common.CookieMemory.API.Post('user.token', token);

      this.loginService.getSession(sessionId).subscribe({
        next: (res: any) => {
          console.log('[SSO Debug] getSession response payload:', res);

          let sessionObj: any = null;
          if (res) {
            if (Array.isArray(res.data) && res.data.length > 0) {
              sessionObj = res.data[0];
            } else if (res.data && typeof res.data === 'object' && !Array.isArray(res.data)) {
              sessionObj = res.data;
            } else if (Array.isArray(res) && res.length > 0) {
              sessionObj = res[0];
            } else if (typeof res === 'object' && (res.user_id || res.userId || res.id)) {
              sessionObj = res;
            }
          }

          if (sessionObj) {
            const resolvedModuleId = moduleId || environment.moduleId || sessionObj.module_id;
            this.common.SessionMemory.API.Post("session.module_id", resolvedModuleId);

            let user = {
              user_id: sessionObj.user_id || sessionObj.userId || sessionObj.id,
              userId: sessionObj.user_id || sessionObj.userId || sessionObj.id,
              token: token,
              name: sessionObj.name || sessionObj.username || '',
              session_id: sessionId,
              module_id: resolvedModuleId
            };

            this.loginService.createSession(user).subscribe({
              next: () => { },
              error: (error: any) => this.logger.error(error)
            });

            this.user_details = user;
            this.storeUserDetails(this.user_details);

            // Sync with AuthService signal so the authGuard doesn't bounce the user back to login!
            this.authService['_currentUser'].set({
              id: this.user_details.userId || this.user_details.user_id,
              username: this.user_details.name,
              name: this.user_details.name,
              token: this.user_details.token,
              sessionId: this.user_details.session_id
            });

            this.common.SessionMemory.API.Post("user", JSON.stringify(sessionObj));
            this.common.SessionMemory.API.Post("user.id", String(user.user_id));
            this.common.SessionMemory.API.Post("user.name", String(user.name));

            this.getPreferencesForUser(user.user_id);
            this.getUserRole(user.user_id);
            this.getUserLocation();
            this.common.notifyUserChange(this.user_details);
            this.isProcessing = false;
          } else {
            console.warn('[SSO Debug] getSession returned empty or invalid data payload:', res);
            this.isNavigating = false;
            this.router.navigate(['/login']);
          }
        },
        error: (error: any) => {
          console.error('[SSO Debug] getSession failed with error:', error);
          this.isNavigating = false;
          if (error.status === -1) {
            this.router.navigate(['/serverdown']);
          } else {
            this.router.navigate(['/login']);
          }
        }
      });
    } else {
      this.isNavigating = false;
      if (Object.keys(this.route.snapshot.queryParams).length > 0 || (this.router.url !== '/login' && this.router.url !== '/')) {
        this.router.navigate(['/login']);
      }
    }
  }


  getPreferencesForUser(userId: string | number): void {
    // Updated to use the Promise-based UsersService we just rewrote
    this.usersService.GetPreferencesByUserId(userId).then(response => {
      this.allPreferences = response.data || response; // Adapt based on actual response shape
      if (Array.isArray(this.allPreferences)) {
        this.storePreferences(this.allPreferences);
      }
    }).catch(error => {
      this.logger.error(error);
    });
  }

  storePreferences(preferencesList: any[]): void {
    for (let i = 0; i < preferencesList.length; i++) {
      let user_preference = preferencesList[i].preference.replace(new RegExp(' ', 'g'), '.');
      if (preferencesList[i].option_id && !preferencesList[i].value) {
        this.common.SessionMemory.API.Post('user.preference.' + user_preference, preferencesList[i].option);
      } else if (preferencesList[i].value && !preferencesList[i].option_id) {
        this.common.SessionMemory.API.Post('user.preference.' + user_preference, preferencesList[i].value);
      }
    }
  }
  findByCopyright(): void {
    this.loginService.findByCopyrightNoAuth().subscribe({
      next: (response: any[]) => {
        const matchedDetails = response.filter(x => x.module_id == environment.moduleId)[0];
        if (matchedDetails) {
          this.copyrightDetails = matchedDetails;
          this.cdr.detectChanges(); // 👈 Tells Angular to update safely
        }
      },
      error: (error: any) => this.logger.error(error)
    });
  }
  getFormData(): void {
    // Swapped to common.CookieMemory
    let cookieData = this.common.CookieMemory.API.Get('user');
    if (cookieData) {
      this.user = JSON.parse(cookieData);
    }
    document.getElementById('username')?.focus();
  }

  getUserRole(userId: string | number): void {
    this.store_manager = false;
    this.loginService.getUserRole(String(userId)).subscribe({
      next: (response: any) => {
        const roles = Array.isArray(response) ? response : (response?.data || []);
        if (roles && Array.isArray(roles) && roles.length > 0) {
          let user_roles = roles.filter((role: any) => role.role_id == 83 || role.id == 83);
          if (user_roles?.length) {
            this.store_manager = true;
          }
          this.common.SessionMemory.API.Post('store_manager', this.store_manager);

          const roleIds = roles.map((role: any) => role.role_id || role.id);
          sessionStorage.setItem('user.roleIds', JSON.stringify(roleIds));
        }
        this.goToDashboard();
      },
      error: (error: any) => {
        this.logger.error('Error fetching user roles:', error);
        this.goToDashboard();
      }
    });
  }




  login(): void {
    // Reset previous error and start loading
    this.login_error = false;
    this.isProcessing = true;
    this.loginButtonText = "Signing in...";
    this.user.module_id = environment.moduleId;

    this.loginService.login(this.user).subscribe({
      next: (response: any) => {
        this.isProcessing = false;
        if (response.status === 200) {
          this.user_details = response.data;
          this.storeUserDetails(this.user_details);
          // Reload AuthService so the auth guard recognizes the login
          this.authService['_currentUser'].set({
            id: this.user_details.userId || this.user_details.user_id,
            username: this.user_details.name,
            name: this.user_details.name,
            token: this.user_details.token,
            sessionId: this.user_details.session_id
          });
          this.getUserRole(this.user_details.user_id);
          this.getUserLocation();
          this.getPreferencesForUser(this.user_details.user_id);
          this.common.notifyUserChange(this.user_details);
        }
      },
      error: (error: any) => {
        // INSTANT ACTION STARTS HERE
        this.isProcessing = false;
        this.loginButtonText = "Sign In";
        this.login_error = true; // This triggers the UI *ngIf immediately

        // Extract the message from the API failure
        this.login_message = error.error?.error?.error ||
          error.error?.message ||
          'Unable to connect to application platform!';

        this.user.password = ""; // Clear password for a fresh attempt

        // Clear specific token and session keys to ensure no stale data,
        // without destructively clearing the entire localStorage/sessionStorage.
        this.common.SessionMemory.API.Post('user.token', null);
        this.common.SessionMemory.API.Post('user.session', null);
        this.common.CookieMemory.API.Clear('user.token');
        this.common.CookieMemory.API.Clear('user.session');

        this.cdr.detectChanges();
        // Help the user by focusing the field back
        document.getElementById('username')?.focus();
      }
    });
  }



  getUserLocation(): void {
    if (navigator.geolocation) {
      const showPosition = (position: GeolocationPosition) => {
        let location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        };
        this.loginService.setUserLocation(location).subscribe({
          next: () => { },
          error: (err) => this.logger.error(err)
        });
      };
      navigator.geolocation.getCurrentPosition(showPosition);
    }
  }

  storeUserDetails(userDetails: any): void {
    try {
      let user = {
        id: userDetails.userId,
        token: userDetails.token,
        name: userDetails.name,
        sessionId: userDetails.session_id
      };
      this.common.SessionMemory.API.Post('user', user);
      this.common.SessionMemory.API.Post('user.id', userDetails.userId);
      this.common.SessionMemory.API.Post('user.token', userDetails.token);
      this.common.SessionMemory.API.Post('user.session', userDetails.session_id);
      //fix
      this.common.CookieMemory.API.Post('user.token', userDetails.token);
      this.common.CookieMemory.API.Post('user.session', userDetails.session_id);
    } catch (error) {
      this.logger.error('Error storing user details', error);
    }
  }

  goToDashboard(): void {
    if (!this.store_manager) {
      this.router.navigate(['/profilemanagement/dashboard'], { state: { userdetails: this.user_details } });
    } else {
      this.router.navigate(['/profilemanagement/users'], { state: { userdetails: this.user_details } });
    }
  }

  cleanUpSession(): void {
    this.common.LocalMemory.API.Clear();
    this.common.SessionMemory.API.Clear();
    //fix
    this.common.CookieMemory.API.Clear('user.token');
    this.common.CookieMemory.API.Clear('user.session');
  }

  logout(): void {
    this.loginService.logout().subscribe({
      next: () => {
        this.cleanUpSession();
        this.common.notifyUserChange(null); // Notify the app the user logged out
        this.router.navigate(['/login']);
      },
      error: (error: any) => {
        this.logger.error('Logout error', error);
      }
    });
  }
}